/**
 * The notice band as the RECEIVING app draws it — for the preview on /notices.
 *
 * Markup and palette are copied from `NoticeBar.tsx` of the What's-New module
 * (pure-app / customer360-app `web/src/whats-new/NoticeBar.tsx`, state of 2026-09-24). This is a
 * copy on purpose, not an import: the module is a folder the consumer apps own (S3), and this
 * app does not carry it. If the module's band changes, this preview has to follow — the preview
 * is only worth something while it looks like the real thing.
 *
 * The dismiss button is drawn but inert: in the target app it hides the band for that reader
 * (fingerprint in localStorage) and the notice stays readable in the panel (D5).
 */
import { AlertTriangle, Info, X } from 'lucide-react';
import type { NoticeSeverity } from '../../types/notices';

const STYLES: Record<NoticeSeverity, { bar: string; icon: string; button: string }> = {
    info: {
        bar: 'border-blue-200 bg-blue-50 text-blue-900',
        icon: 'text-blue-600',
        button: 'text-blue-700 hover:bg-blue-100',
    },
    warning: {
        bar: 'border-amber-200 bg-amber-50 text-amber-900',
        icon: 'text-amber-600',
        button: 'text-amber-800 hover:bg-amber-100',
    },
    critical: {
        bar: 'border-red-200 bg-red-50 text-red-900',
        icon: 'text-red-600',
        button: 'text-red-800 hover:bg-red-100',
    },
};

interface Props {
    severity: NoticeSeverity;
    title: string;
    body: string;
}

export function NoticeBandPreview({ severity, title, body }: Props) {
    const style = STYLES[severity] ?? STYLES.info;
    const Icon = severity === 'info' ? Info : AlertTriangle;
    const emptyTitle = title.trim() === '';
    const emptyBody = body.trim() === '';
    return (
        <div className="overflow-hidden rounded-md border border-gray-200" data-testid="notice-preview">
            {/* A strip of "app header" above the band, so it reads as the top of a page. */}
            <div className="flex items-center gap-2 border-b border-gray-200 bg-white px-4 py-2">
                <div className="h-2.5 w-2.5 rounded-full bg-gray-300" />
                <div className="h-2 w-24 rounded bg-gray-200" />
            </div>
            <div className={`flex items-start gap-3 border-b px-6 py-2.5 ${style.bar}`}>
                <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${style.icon}`} aria-hidden="true" />
                <div className="min-w-0 flex-1">
                    <p className={`text-sm font-bold leading-tight ${emptyTitle ? 'opacity-40' : ''}`}>
                        {emptyTitle ? 'Title of the notice' : title.trim()}
                    </p>
                    <p className={`mt-0.5 text-sm leading-relaxed ${emptyBody ? 'opacity-40' : ''}`}>
                        {emptyBody ? 'The text readers will see under the title.' : body.trim()}
                    </p>
                </div>
                <span className={`-mr-1 shrink-0 rounded-md p-1.5 ${style.button}`} aria-hidden="true">
                    <X className="h-4 w-4" />
                </span>
            </div>
            <div className="space-y-1.5 bg-gray-50 px-4 py-3">
                <div className="h-2 w-2/3 rounded bg-gray-200" />
                <div className="h-2 w-1/2 rounded bg-gray-200" />
            </div>
        </div>
    );
}
